import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { LOGO_SQUARE } from '../../utils/brand';

const DISMISSED_KEY = 'outlaw_pwa_install_dismissed_v1';

const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISSED_KEY)) return;
    } catch {
      /* ignore */
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const installedHandler = () => {
      // Fire-and-forget install tracking + welcome push subscription
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        if (API) {
          fetch(`${API}/api/track/pwa-install`, { method: 'POST' }).catch(() => {});
        }
        localStorage.setItem(DISMISSED_KEY, '1');
        localStorage.setItem('outlaw_pwa_installed', '1');
      } catch { /* ignore */ }
      // Auto-prompt push subscription on install - silent if browser denies
      try {
        import('../../utils/push').then(({ subscribeToPush }) => {
          subscribeToPush?.().catch(() => {});
        });
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    if (outcome === 'accepted') {
      try {
        localStorage.setItem(DISMISSED_KEY, '1');
      } catch {
        /* ignore */
      }
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-3 right-3 bottom-3 sm:left-6 sm:right-auto sm:bottom-24 sm:max-w-sm z-40 bg-zinc-950/95 border border-zinc-800 rounded-lg p-4 animate-pop-up"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="install-prompt-dismiss"
        aria-label="Dismiss"
        className="absolute top-2 right-2 w-7 h-7 rounded-full text-gray-500 hover:text-white hover:bg-zinc-800 flex items-center justify-center"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 mb-3 pr-6">
        <div className="w-14 h-14 rounded-xl bg-black border border-red-500/40 overflow-hidden flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(220,38,38,0.45)]">
          <img
            src={LOGO_SQUARE}
            alt="Outlaw Media"
            className="w-full h-auto object-contain p-1"
          />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-black tracking-[0.25em] text-red-400 uppercase mb-1">
            One-tap access
          </p>
          <p className="text-base font-black text-white leading-tight">
            Install Outlaw Media on your home screen
          </p>
          <p className="text-xs text-gray-400 leading-snug mt-1">
            Push alerts on every new drop. Instant launch. No app store.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={install}
          data-testid="install-prompt-accept"
          className="flex-1 bg-red-600 hover:bg-red-500 text-white font-black tracking-widest uppercase text-xs px-4 py-2.5 rounded-lg shadow-[0_0_18px_rgba(220,38,38,0.6)] hover:shadow-[0_0_28px_rgba(220,38,38,0.85)] transition-all"
        >
          Install Now
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="install-prompt-later"
          className="text-[10px] font-bold tracking-widest uppercase text-gray-500 hover:text-white px-2"
        >
          Later
        </button>
      </div>

      <style>{`
        @keyframes pop-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-pop-up { animation: pop-up 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default InstallPrompt;
